import React, { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import SegmentedControl from "../../../components/mobile/segmented-control";
import { DashboardMonthlyTrend } from "../../../lib/api";
import { formatCompact, formatMonthLabel, formatNumber } from "./format";
import { MobileCard, SectionHeader } from "./section";

type TrendView = "activity" | "revenue";

const ACTIVITY_SERIES = [
  { key: "quotations.count", label: "Quotations", color: "#53A9FB" },
  { key: "invoices.count", label: "Invoices", color: "#A78BFA" },
  { key: "movements", label: "Movements", color: "#FB923C" },
  { key: "customers", label: "New customers", color: "#F87171" },
];

const REVENUE_SERIES = [
  { key: "quotations.total", label: "Quotations", color: "#53A9FB" },
  { key: "invoices.total", label: "Invoiced", color: "#A78BFA" },
  { key: "invoices.paid", label: "Paid", color: "#22C55E" },
];

const axisTick = { fontSize: 11, fill: "#8C95A4" };

const TrendTooltip = ({ active, payload, label, isMoney }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-gray-100 bg-white px-3 py-2 shadow-lg">
      <p className="mb-1 text-xs font-semibold text-gray-900">{label}</p>
      {payload.map((entry: any) => (
        <div key={entry.dataKey} className="flex items-center gap-2 text-xs">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-gray-500">{entry.name}</span>
          <span className="ml-auto pl-3 font-semibold text-gray-900">
            {isMoney ? `TZS ${formatNumber(entry.value)}` : formatNumber(entry.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

const Legend: React.FC<{ series: typeof ACTIVITY_SERIES }> = ({ series }) => (
  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
    {series.map((item) => (
      <span key={item.key} className="flex items-center gap-1.5 text-[11px] text-gray-600">
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
        {item.label}
      </span>
    ))}
  </div>
);

const TrendsCard: React.FC<{ monthlyTrend: DashboardMonthlyTrend | null }> = ({
  monthlyTrend,
}) => {
  const [view, setView] = useState<TrendView>("activity");
  const trends = monthlyTrend?.trends ?? [];
  if (trends.length === 0) return null;

  const data = trends.map((row) => ({ ...row, label: formatMonthLabel(row.month) }));
  const series = view === "activity" ? ACTIVITY_SERIES : REVENUE_SERIES;

  return (
    <section>
      <SectionHeader
        title="Monthly trends"
        subtitle={`Last ${monthlyTrend?.period ?? trends.length} months`}
      />
      <MobileCard>
        <SegmentedControl
          value={view}
          onChange={setView}
          options={[
            { value: "activity", label: "Activity" },
            { value: "revenue", label: "Revenue" },
          ]}
        />
        <div className="mt-4 h-56 w-full -ml-2">
          <ResponsiveContainer width="100%" height="100%">
            {view === "activity" ? (
              <BarChart data={data} barCategoryGap="22%" barGap={2}>
                <CartesianGrid vertical={false} stroke="#F1F1F3" />
                <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} />
                <YAxis
                  tick={axisTick}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                  width={32}
                  tickFormatter={(value: number) => formatCompact(value)}
                />
                <Tooltip content={<TrendTooltip />} cursor={{ fill: "#F7F6F8" }} />
                {ACTIVITY_SERIES.map((item) => (
                  <Bar
                    key={item.key}
                    dataKey={item.key}
                    name={item.label}
                    fill={item.color}
                    radius={[3, 3, 0, 0]}
                    maxBarSize={10}
                  />
                ))}
              </BarChart>
            ) : (
              <LineChart data={data}>
                <CartesianGrid vertical={false} stroke="#F1F1F3" />
                <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} />
                <YAxis
                  tick={axisTick}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                  tickFormatter={(value: number) => formatCompact(value)}
                />
                <Tooltip content={<TrendTooltip isMoney />} />
                {REVENUE_SERIES.map((item) => (
                  <Line
                    key={item.key}
                    type="monotone"
                    dataKey={item.key}
                    name={item.label}
                    stroke={item.color}
                    strokeWidth={2.5}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                ))}
              </LineChart>
            )}
          </ResponsiveContainer>
        </div>
        <Legend series={series} />
      </MobileCard>
    </section>
  );
};

export default TrendsCard;
